"use client";

import { c as _c } from "react/compiler-runtime";
import { CreateDropWaveWrapper } from "@/components/waves/CreateDropWaveWrapper";
import { WaveDropsAllWithoutProvider } from "@/components/waves/drops/wave-drops-all";
import { WaveGallery } from "@/components/waves/gallery";
import MobileMemesArtSubmissionBtn from "@/components/waves/memes/submission/MobileMemesArtSubmissionBtn";
import PrivilegedDropCreator, { DropMode } from "@/components/waves/PrivilegedDropCreator";
import { useNotificationsContext } from "@/components/notifications/NotificationsContext";
import { UnreadDividerProvider, useUnreadDivider } from "@/contexts/wave/UnreadDividerContext";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiWave } from "@/generated/models/ApiWave";
import { getHomeRoute } from "@/helpers/navigation.helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { useWave } from "@/hooks/useWave";
import type { WaveViewMode } from "@/hooks/useWaveViewMode";
import { selectEditingDropId } from "@/store/editSlice";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import { ActiveDropAction } from "@/types/dropInteractionTypes";
import { commonApiPostWithoutBodyAndResponse } from "@/services/api/common-api";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLayout } from "./layout/LayoutContext";
interface InitialDropState {
  readonly waveId: string;
  readonly serialNo: number;
  readonly dividerSerialNo: number | null;
}
interface MyStreamWaveChatProps {
  readonly wave: ApiWave;
  readonly firstUnreadSerialNo: number | null;
  readonly viewMode: WaveViewMode;
  readonly onDropClick: (drop: ExtendedDrop) => void;
}
interface WaveChatLeaveHandlerProps {
  readonly waveId: string;
}
const WaveChatLeaveHandler: React.FC<WaveChatLeaveHandlerProps> = t0 => {
  const $ = _c(5);
  const {
    waveId
  } = t0;
  const {
    setUnreadDividerSerialNo
  } = useUnreadDivider();
  const {
    removeWaveDeliveredNotifications
  } = useNotificationsContext();
  let t1;
  let t2;
  if ($[0] !== removeWaveDeliveredNotifications || $[1] !== setUnreadDividerSerialNo || $[2] !== waveId) {
    t1 = () => () => {
      setUnreadDividerSerialNo(null);
      (async () => {
        ;
        try {
          await Promise.resolve(removeWaveDeliveredNotifications(waveId));
          await commonApiPostWithoutBodyAndResponse({
            endpoint: `notifications/wave/${waveId}/read`
          });
        } catch (t3) {
          const error = t3;
          console.error("Failed to mark feed as read:", error);
        }
      })();
    };
    t2 = [waveId, setUnreadDividerSerialNo, removeWaveDeliveredNotifications];
    $[0] = removeWaveDeliveredNotifications;
    $[1] = setUnreadDividerSerialNo;
    $[2] = waveId;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  return null;
};
const MyStreamWaveChat: React.FC<MyStreamWaveChatProps> = t0 => {
  const $ = _c(64);
  const {
    wave,
    firstUnreadSerialNo,
    viewMode,
    onDropClick
  } = t0;
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const containerRef = useRef(null);
  const {
    isMemesWave
  } = useWave(wave);
  const editingDropId = useSelector(selectEditingDropId);
  const {
    isApp
  } = useDeviceInfo();
  let t1;
  if ($[0] !== wave.id) {
    t1 = {
      waveId: wave.id,
      activeDrop: null
    };
    $[0] = wave.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [activeDropState, setActiveDropState] = useState(t1);
  const activeDrop = activeDropState.waveId === wave.id ? activeDropState.activeDrop : null;
  let t2;
  if ($[2] !== wave.id) {
    t2 = nextActiveDrop => {
      setActiveDropState({
        waveId: wave.id,
        activeDrop: nextActiveDrop
      });
    };
    $[2] = wave.id;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const setActiveDropForWave = t2;
  let t3;
  if ($[4] !== firstUnreadSerialNo || $[5] !== searchParams || $[6] !== wave.id) {
    bb0: {
      const dropParam = searchParams.get("serialNo");
      if (!dropParam) {
        t3 = null;
        break bb0;
      }
      const parsed = Number.parseInt(dropParam, 10);
      if (!Number.isFinite(parsed)) {
        t3 = null;
        break bb0;
      }
      let t4;
      if ($[8] !== firstUnreadSerialNo || $[9] !== searchParams) {
        const dividerParam = searchParams.get("divider");
        const dividerParsed = dividerParam ? Number.parseInt(dividerParam, 10) : null;
        t4 = dividerParsed !== null && Number.isFinite(dividerParsed) ? dividerParsed : firstUnreadSerialNo;
        $[8] = firstUnreadSerialNo;
        $[9] = searchParams;
        $[10] = t4;
      } else {
        t4 = $[10];
      }
      const dividerSerialNo = t4;
      let t5;
      if ($[11] !== dividerSerialNo || $[12] !== parsed || $[13] !== wave.id) {
        t5 = {
          waveId: wave.id,
          serialNo: parsed,
          dividerSerialNo
        };
        $[11] = dividerSerialNo;
        $[12] = parsed;
        $[13] = wave.id;
        $[14] = t5;
      } else {
        t5 = $[14];
      }
      t3 = t5;
    }
    $[4] = firstUnreadSerialNo;
    $[5] = searchParams;
    $[6] = wave.id;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const initialDropState = t3;
  let t4;
  if ($[15] !== firstUnreadSerialNo || $[16] !== initialDropState || $[17] !== wave.id) {
    t4 = () => {
      if (initialDropState?.waveId === wave.id && initialDropState.dividerSerialNo !== null) {
        return {
          waveId: wave.id,
          serialNo: initialDropState.dividerSerialNo
        };
      }
      return {
        waveId: wave.id,
        serialNo: firstUnreadSerialNo
      };
    };
    $[15] = firstUnreadSerialNo;
    $[16] = initialDropState;
    $[17] = wave.id;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const [capturedDividerState] = useState(t4);
  const scrollTarget = initialDropState?.waveId === wave.id ? initialDropState.serialNo : null;
  let dividerTarget = firstUnreadSerialNo;
  if (initialDropState?.waveId === wave.id) {
    dividerTarget = initialDropState.dividerSerialNo;
  } else {
    if (capturedDividerState.waveId === wave.id) {
      dividerTarget = capturedDividerState.serialNo;
    }
  }
  let t5;
  let t6;
  if ($[19] !== initialDropState || $[20] !== pathname || $[21] !== router || $[22] !== searchParams) {
    t5 = () => {
      if (!initialDropState) {
        return;
      }
      const params = new URLSearchParams(searchParams.toString() || "");
      if (!params.has("serialNo") && !params.has("divider")) {
        return;
      }
      params.delete("serialNo");
      params.delete("divider");
      const href = params.toString() ? `${pathname}?${params.toString()}` : pathname || getHomeRoute();
      router.replace(href, {
        scroll: false
      });
    };
    t6 = [initialDropState, searchParams, router, pathname];
    $[19] = initialDropState;
    $[20] = pathname;
    $[21] = router;
    $[22] = searchParams;
    $[23] = t5;
    $[24] = t6;
  } else {
    t5 = $[23];
    t6 = $[24];
  }
  useEffect(t5, t6);
  const {
    waveViewStyle
  } = useLayout();
  let t7;
  if ($[25] !== setActiveDropForWave) {
    const onReply = (drop, partId) => {
      setActiveDropForWave({
        action: ActiveDropAction.REPLY,
        drop,
        partId
      });
    };
    t7 = t8 => {
      const {
        drop: drop_0,
        partId: partId_0
      } = t8;
      onReply(drop_0, partId_0);
    };
    $[25] = setActiveDropForWave;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  const handleReply = t7;
  let t8;
  if ($[27] !== setActiveDropForWave) {
    t8 = () => {
      setActiveDropForWave(null);
    };
    $[27] = setActiveDropForWave;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  const onCancelReplyQuote = t8;
  if (viewMode === "gallery") {
    let t9;
    if ($[29] !== onDropClick || $[30] !== wave) {
      t9 = <WaveGallery wave={wave} onDropClick={onDropClick} />;
      $[29] = onDropClick;
      $[30] = wave;
      $[31] = t9;
    } else {
      t9 = $[31];
    }
    let t10;
    if ($[32] !== t9 || $[33] !== waveViewStyle) {
      t10 = <div ref={containerRef} className="tw-flex tw-h-full tw-w-full tw-flex-col tw-overflow-hidden" style={waveViewStyle}>{t9}</div>;
      $[32] = t9;
      $[33] = waveViewStyle;
      $[34] = t10;
    } else {
      t10 = $[34];
    }
    return t10;
  }
  const t9 = dividerTarget ?? null;
  const t10 = `unread-divider-${wave.id}`;
  let t11;
  if ($[35] !== wave.id) {
    t11 = <WaveChatLeaveHandler waveId={wave.id} />;
    $[35] = wave.id;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  let t12;
  if ($[37] !== activeDrop || $[38] !== dividerTarget || $[39] !== handleReply || $[40] !== scrollTarget || $[41] !== wave.id || $[42] !== wave.metrics.muted || $[43] !== wave.metrics.your_unread_drops_count) {
    t12 = <WaveDropsAllWithoutProvider key={wave.id} waveId={wave.id} onReply={handleReply} activeDrop={activeDrop} initialDrop={scrollTarget} dividerSerialNo={dividerTarget} unreadCount={wave.metrics.your_unread_drops_count} dropId={null} isMuted={wave.metrics.muted} />;
    $[37] = activeDrop;
    $[38] = dividerTarget;
    $[39] = handleReply;
    $[40] = scrollTarget;
    $[41] = wave.id;
    $[42] = wave.metrics.muted;
    $[43] = wave.metrics.your_unread_drops_count;
    $[44] = t12;
  } else {
    t12 = $[44];
  }
  let t13;
  if ($[45] !== activeDrop || $[46] !== editingDropId || $[47] !== isApp || $[48] !== onCancelReplyQuote || $[49] !== wave) {
    t13 = !(isApp && editingDropId) && <div className="tw-mt-auto"><CreateDropWaveWrapper><PrivilegedDropCreator activeDrop={activeDrop} onCancelReplyQuote={onCancelReplyQuote} onDropAddedToQueue={onCancelReplyQuote} wave={wave} dropId={null} fixedDropMode={DropMode.BOTH} /></CreateDropWaveWrapper></div>;
    $[45] = activeDrop;
    $[46] = editingDropId;
    $[47] = isApp;
    $[48] = onCancelReplyQuote;
    $[49] = wave;
    $[50] = t13;
  } else {
    t13 = $[50];
  }
  let t14;
  if ($[51] !== isMemesWave || $[52] !== wave) {
    t14 = isMemesWave && <MobileMemesArtSubmissionBtn wave={wave} />;
    $[51] = isMemesWave;
    $[52] = wave;
    $[53] = t14;
  } else {
    t14 = $[53];
  }
  let t15;
  if ($[54] !== t12 || $[55] !== t13 || $[56] !== t14 || $[57] !== waveViewStyle) {
    t15 = <div ref={containerRef} className="tw-w-full tw-flex tw-flex-col tw-overflow-y-auto tw-overflow-x-hidden lg:tw-scrollbar-thin tw-scrollbar-thumb-iron-500 tw-scrollbar-track-iron-800 desktop-hover:hover:tw-scrollbar-thumb-iron-300 scroll-shadow tw-flex-grow" style={waveViewStyle}>{t12}{t13}{t14}</div>;
    $[54] = t12;
    $[55] = t13;
    $[56] = t14;
    $[57] = waveViewStyle;
    $[58] = t15;
  } else {
    t15 = $[58];
  }
  let t16;
  if ($[59] !== t10 || $[60] !== t11 || $[61] !== t15 || $[62] !== t9) {
    t16 = <UnreadDividerProvider initialSerialNo={t9} key={t10}>{t11}{t15}</UnreadDividerProvider>;
    $[59] = t10;
    $[60] = t11;
    $[61] = t15;
    $[62] = t9;
    $[63] = t16;
  } else {
    t16 = $[63];
  }
  return t16;
};
export default MyStreamWaveChat;